import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { FileText, Image, Link as LinkIcon, ListPlus, Loader2, Plus, Sparkles, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { BulkLinksPasteSheet } from "@/components/agent/details/BulkLinksPasteSheet";
import { KbResourceSuggestions } from "@/components/agent/details/KbResourceSuggestions";
import { KnowledgeLinkSuggestions } from "@/components/agent/details/KnowledgeLinkSuggestions";
import { ResourceDescriptionField } from "@/components/agent/details/ResourceDescriptionField";
import { ResourceDocumentThumb, ResourceLinkThumb } from "@/components/agent/details/ResourceThumb";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { Button } from "@/components/ui/button";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import {
  documentDescribeItem,
  linkDescribeItem,
  needsDescription,
  useDescribeResources,
} from "@/hooks/agent/useDescribeResources";
import { RESOURCE_DESCRIPTION_MAX } from "@/lib/agent/bulkInfoLinks";
import { removeResourceFromStorage, uploadResourceToStorage } from "@/lib/uploadResourceToStorage";
import {
  AgentResourcesService,
  type DescribeResourceResult,
  type LinkCheckResult,
} from "@/services/agent/AgentResourcesService";
import { Agent } from "@/types/agent/agent";
import { InfoLink, InfoLinksConfig, ResourceDocument } from "@/types/agent/info-links";

const MAX_LINKS = 100;
const MAX_DOCUMENTS = 50;
const MAX_DOC_BYTES = 25 * 1024 * 1024; // 25 MB — matches the storage bucket limit

const formatSize = (bytes?: number): string | null => {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const stripExtension = (fileName: string): string => fileName.replace(/\.[^./\\]+$/, "");

const DOCUMENT_DESCRIPTION_PLACEHOLDER =
  "What's in it and when to show it — e.g. \"Diagram of the switchboard, for wiring or breaker questions\"";
const LINK_DESCRIPTION_PLACEHOLDER = "What this page covers and when a customer needs it (optional)";
const AI_BUTTON_CLASS =
  "gap-1.5 bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold shadow-md shadow-genie-primary/30 transition-all duration-200 disabled:opacity-60";

type DescribableFields =Partial<Pick<InfoLink, "label" | "description" | "descriptionSource">>;

const fillDescription = <T extends { id: string; description?: string | null }>(
  item: T,
  byId: Map<string, DescribeResourceResult>,
): T => {
  const result = byId.get(item.id);
  return result?.description && needsDescription(item)
    ? ({ ...item, description: result.description, descriptionSource: "ai" } as T)
    : item;
};

interface InfoLinksCardProps {
  agent: Agent;
  brand?: string;
}

export const InfoLinksCard = ({ agent, brand = "#4E9CFF" }: InfoLinksCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newLinkLabel, setNewLinkLabel] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [newLinkDescription, setNewLinkDescription] = useState("");
  const [linkUrlError, setLinkUrlError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [showBulkPaste, setShowBulkPaste] = useState(false);
  const [labelSuggestions, setLabelSuggestions] = useState<Record<string, string>>({});
  const [nameOnlyIds, setNameOnlyIds] = useState<ReadonlySet<string>>(new Set());
  const [unreachableIds, setUnreachableIds] = useState<ReadonlySet<string>>(new Set());
  const isUploading = uploadProgress !== null;
  const draftLinkIdRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateAgent = useUpdateAgent();

  // Stored info_links can lack `links` (a genie with only documents or a banner).
  const getRawConfig = (): InfoLinksConfig => {
    const stored = (agent.page?.info_links ?? agent.info_links) as Partial<InfoLinksConfig> | undefined;
    return { ...stored, links: stored?.links ?? [] };
  };

  const [config, setConfig] = useState<InfoLinksConfig>(getRawConfig);

  const infoLinks = config.links ?? [];
  const committedLinks = infoLinks.filter((l) => l.id !== draftLinkIdRef.current);
  const documents = config.documents ?? [];
  const bannerImageUrl = config.banner_image_url ?? "";

  const applyReachability = (results: LinkCheckResult[]) =>
    setUnreachableIds((prev) => {
      const next = new Set(prev);
      results.forEach((result) => {
        if (result.reachable === false) next.add(result.id);
        else if (result.reachable) next.delete(result.id);
      });
      return next;
    });

  // Only fills descriptions that are still empty — anything the owner typed
  // while the AI was reading wins.
  const applyDescriptions = (results: DescribeResourceResult[]) => {
    applyReachability(results);
    const byId = new Map(results.map((result) => [result.id, result]));
    setConfig((prev) => ({
      ...prev,
      links: prev.links.map((link) => fillDescription(link, byId)),
      documents: (prev.documents ?? []).map((doc) => fillDescription(doc, byId)),
    }));
    setLabelSuggestions((prev) => {
      const next = { ...prev };
      results.forEach((result) => {
        if (result.suggestedLabel) next[result.id] = result.suggestedLabel;
      });
      return next;
    });
    setNameOnlyIds((prev) => new Set([...prev, ...results.filter((r) => r.basis === "name").map((r) => r.id)]));
  };

  const { describe, pendingIds, isDescribing } = useDescribeResources(agent.id, applyDescriptions);

  const describeMissing = (links: InfoLink[], docs: ResourceDocument[]) =>
    void describe([
      ...docs.filter(needsDescription).map(documentDescribeItem),
      ...links.filter(needsDescription).map(linkDescribeItem),
    ]);

  // Describing a link opens it, so only links that already have a description need a separate check.
  const reviewNewLinks = (links: InfoLink[]) => {
    describeMissing(links, []);
    const described = links.filter((link) => !needsDescription(link)).map(({ id, url }) => ({ id, url }));
    if (!agent.id || described.length === 0) return;
    AgentResourcesService.checkLinks(agent.id, described)
      .then(applyReachability)
      .catch((error) => console.error("Checking links failed", error));
  };

  const missingCount = [...committedLinks, ...documents].filter(
    (resource) => needsDescription(resource) && !pendingIds.has(resource.id),
  ).length;

  // Matches KbResourceCandidate.key: the document id, or id/fileName for one picture of a batch.
  const offeredKeys = useMemo(
    () =>
      new Set(
        documents.flatMap((doc) =>
          doc.sourceDocumentId ? [doc.sourceDocumentId, `${doc.sourceDocumentId}/${doc.fileName}`] : [],
        ),
      ),
    [documents],
  );

  // Catches knowledge-base files uploaded straight into Resources, which carry no sourceDocumentId.
  const offeredFileNames = useMemo(() => new Set(documents.map((doc) => doc.fileName.toLowerCase())), [documents]);

  const validateLinkUrl = (url: string): boolean => {
    if (!url) {
      setLinkUrlError(null);
      return false;
    }
    try {
      new URL(url, window.location.origin);
      setLinkUrlError(null);
      return true;
    } catch {
      setLinkUrlError("Please enter a valid URL");
      return false;
    }
  };

  useEffect(() => {
    if (!isEditing) return;
    const label = newLinkLabel.trim();
    const url = newLinkUrl.trim();
    const description = newLinkDescription.trim() || null;
    const draftId = draftLinkIdRef.current;

    if (!label || !url) {
      if (draftId) {
        setConfig((prev) => ({ ...prev, links: prev.links.filter((l) => l.id !== draftId) }));
        draftLinkIdRef.current = null;
      }
      return;
    }
    if (!validateLinkUrl(url)) return;

    if (!draftLinkIdRef.current) draftLinkIdRef.current = `link-${Date.now()}`;
    const nextDraft: InfoLink = { id: draftLinkIdRef.current, label, url, description };

    setConfig((prev) => {
      const existing = prev.links.find((l) => l.id === draftLinkIdRef.current);
      if (
        existing?.label === nextDraft.label &&
        existing?.url === nextDraft.url &&
        (existing?.description ?? null) === nextDraft.description
      ) {
        return prev;
      }
      return { ...prev, links: [...prev.links.filter((l) => l.id !== draftLinkIdRef.current), nextDraft] };
    });
  }, [newLinkLabel, newLinkUrl, newLinkDescription, isEditing]);

  const resetNewLink = () => {
    draftLinkIdRef.current = null;
    setNewLinkLabel("");
    setNewLinkUrl("");
    setNewLinkDescription("");
  };

  const handleEdit = () => {
    setConfig(getRawConfig());
    setNewLinkLabel("");
    setNewLinkUrl("");
    setNewLinkDescription("");
    setLinkUrlError(null);
    setLabelSuggestions({});
    setNameOnlyIds(new Set());
    setUnreachableIds(new Set());
    draftLinkIdRef.current = null;
    setIsEditing(true);
  };

  const handleDescribeSaved = () => {
    handleEdit();
    const raw = getRawConfig();
    describeMissing(raw.links ?? [], raw.documents ?? []);
  };

  const handleCancel = () => setIsEditing(false);

  const handleSave = async () => {
    if (isDescribing) {
      toast.info(`Still writing ${pendingIds.size} description${pendingIds.size === 1 ? "" : "s"} — save again in a moment.`);
      return;
    }
    const finalLinks = infoLinks
      .filter((l) => l.id !== draftLinkIdRef.current || (l.label && l.url))
      .map((l) => ({ ...l, label: l.label.trim() || l.url, description: l.description?.trim() || null }));
    const finalDocuments = documents.map((d) => ({ ...d, description: d.description?.trim() || null }));
    const finalConfig: InfoLinksConfig = { ...config, links: finalLinks, documents: finalDocuments };
    await updateAgent.mutateAsync({
      agent,
      updateData: { page: { ...((agent as any).page ?? {}), info_links: finalConfig } },
    });
    toast.success("Resources saved");
    resetNewLink();
    setIsEditing(false);
  };

  const handleAddLink = () => {
    if (!newLinkLabel.trim() || !newLinkUrl.trim()) return;
    if (!validateLinkUrl(newLinkUrl)) return;
    if (infoLinks.length >= MAX_LINKS) return;

    const id = draftLinkIdRef.current ?? `link-${Date.now()}`;
    const newLink: InfoLink = {
      id,
      label: newLinkLabel.trim(),
      url: newLinkUrl.trim(),
      description: newLinkDescription.trim() || null,
      ...(newLinkDescription.trim() ? { descriptionSource: "owner" as const } : {}),
    };
    setConfig((prev) => ({ ...prev, links: [...prev.links.filter((l) => l.id !== id), newLink] }));
    resetNewLink();
    setLinkUrlError(null);
    reviewNewLinks([newLink]);
  };

  const handleUpdateLink = (id: string, changes: DescribableFields) => {
    setConfig((prev) => ({ ...prev, links: prev.links.map((l) => (l.id === id ? { ...l, ...changes } : l)) }));
  };

  const dismissLabelSuggestion = (id: string) =>
    setLabelSuggestions((prev) => {
      const { [id]: _dismissed, ...rest } = prev;
      return rest;
    });

  const handleRemoveLink = (linkId: string) => {
    setConfig((prev) => ({ ...prev, links: prev.links.filter((l) => l.id !== linkId) }));
  };

  // A complete link still sitting in the "new link" inputs is kept as a
  // regular link alongside the pasted ones, and the inputs are cleared.
  const handleApplyBulkLinks = (links: InfoLink[]) => {
    const draft = infoLinks.find((l) => l.id === draftLinkIdRef.current);
    const knownIds = new Set(committedLinks.map((l) => l.id));
    setConfig((prev) => ({ ...prev, links: draft && links.length < MAX_LINKS ? [...links, draft] : links }));
    resetNewLink();
    setLinkUrlError(null);
    reviewNewLinks(links.filter((l) => !knownIds.has(l.id)));
  };

  const handleAddKnowledgeLinks = (links: InfoLink[]) => {
    setConfig((prev) => ({ ...prev, links: [...prev.links, ...links] }));
    reviewNewLinks(links);
  };

  const handleDismissKnowledgeLinks = (urlKeys: string[]) =>
    setConfig((prev) => ({ ...prev, dismissedLinks: [...new Set([...(prev.dismissedLinks ?? []), ...urlKeys])] }));

  const handleImportedFromKb =(docs: ResourceDocument[]) => {
    setConfig((prev) => ({ ...prev, documents: [...(prev.documents ?? []), ...docs] }));
    describeMissing([], docs);
  };

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = ""; // allow re-selecting the same file later
    if (files.length === 0) return;

    const slots = MAX_DOCUMENTS - documents.length;
    if (slots <= 0) {
      toast.error(`Maximum of ${MAX_DOCUMENTS} documents reached`);
      return;
    }

    const tooLarge = files.filter((file) => file.size > MAX_DOC_BYTES);
    const accepted = files.filter((file) => file.size <= MAX_DOC_BYTES);
    const queue = accepted.slice(0, slots);
    if (tooLarge.length > 0) {
      toast.error(
        tooLarge.length === 1
          ? `${tooLarge[0].name} is too large. Please keep documents under 25 MB.`
          : `${tooLarge.length} files are over 25 MB and were skipped.`,
      );
    }
    if (accepted.length > queue.length) {
      toast.error(`Only ${slots} more document${slots === 1 ? "" : "s"} can be added — the rest were skipped.`);
    }
    if (queue.length === 0) return;

    const failed: string[] = [];
    const uploadedDocs: ResourceDocument[] = [];
    for (const [index, file] of queue.entries()) {
      setUploadProgress({ current: index + 1, total: queue.length });
      try {
        const uploaded = await uploadResourceToStorage(file, agent.id ?? "");
        const doc: ResourceDocument = {
          id: `doc-${Date.now()}-${index}`,
          label: stripExtension(uploaded.fileName),
          fileUrl: uploaded.fileUrl,
          storagePath: uploaded.storagePath,
          fileName: uploaded.fileName,
          mimeType: uploaded.mimeType,
          sizeBytes: uploaded.sizeBytes,
        };
        setConfig((prev) => ({ ...prev, documents: [...(prev.documents ?? []), doc] }));
        uploadedDocs.push(doc);
      } catch (error) {
        console.error("Resource upload failed", error);
        failed.push(file.name);
      }
    }
    setUploadProgress(null);
    describeMissing([], uploadedDocs);

    if (failed.length > 0) {
      toast.error(
        failed.length === 1
          ? `Could not upload ${failed[0]}. Please try again.`
          : `${failed.length} documents could not be uploaded. Please try again.`,
      );
    }
  };

  const handleUpdateDocument = (id: string, changes: DescribableFields) => {
    setConfig((prev) => ({
      ...prev,
      documents: (prev.documents ?? []).map((d) => (d.id === id ? { ...d, ...changes } : d)),
    }));
  };

  const handleRemoveDocument = async (doc: ResourceDocument) => {
    setConfig((prev) => ({ ...prev, documents: (prev.documents ?? []).filter((d) => d.id !== doc.id) }));
    try {
      await removeResourceFromStorage(doc.storagePath);
    } catch (error) {
      // The reference is already gone from the config; a failed storage delete
      // just leaves an orphaned file, which isn't worth blocking the user on.
      console.error("Failed to remove resource file from storage", error);
    }
  };

  const savedConfig = getRawConfig();
  const savedLinks = savedConfig.links ?? [];
  const savedDocuments = savedConfig.documents ?? [];
  const savedMissingCount = [...savedLinks, ...savedDocuments].filter(needsDescription).length;

  const renderDescription = (
    resource: InfoLink | ResourceDocument,
    placeholder: string,
    onUpdate: (id: string, changes: DescribableFields) => void,
  ) => {
    const suggestion = labelSuggestions[resource.id];
    const showSuggestion = !!suggestion && suggestion.trim().toLowerCase() !== resource.label.trim().toLowerCase();
    return (
      <ResourceDescriptionField
        value={resource.description ?? ""}
        onChange={(description) => onUpdate(resource.id, { description, descriptionSource: "owner" })}
        placeholder={placeholder}
        source={resource.descriptionSource}
        pending={pendingIds.has(resource.id)}
        nameOnly={nameOnlyIds.has(resource.id)}
        unreachable={unreachableIds.has(resource.id)}
        suggestedLabel={showSuggestion ? suggestion : null}
        onAcceptLabel={() => {
          onUpdate(resource.id, { label: suggestion });
          dismissLabelSuggestion(resource.id);
        }}
        onDismissLabel={() => dismissLabelSuggestion(resource.id)}
      />
    );
  };

  return (
    <OverviewSectionCard
      title="Resources"
      subtitle="Documents and links shown on your live agent page"
      genieIcon="paperclip"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
      onEdit={isEditing ? undefined : handleEdit}
      onSave={isEditing ? handleSave : undefined}
      onCancel={isEditing ? handleCancel : undefined}
      isSaving={updateAgent.isPending}
    >
      {isEditing ? (
        <div className="space-y-5">
          {(missingCount > 0 || isDescribing) && (
            <div className="flex flex-col gap-2 rounded-lg bg-genie-primary-light/60 p-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-gray-600">
                {isDescribing
                  ? `Reading ${pendingIds.size} resource${pendingIds.size === 1 ? "" : "s"} and writing descriptions…`
                  : `${missingCount} resource${missingCount === 1 ? " has" : "s have"} no description yet. Let AI read ${missingCount === 1 ? "it" : "them"} — diagrams and scans included.`}
              </p>
              <Button
                type="button"
                size="sm"
                onClick={() => describeMissing(committedLinks, documents)}
                disabled={isDescribing || missingCount === 0}
                className={AI_BUTTON_CLASS}
              >
                {isDescribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {isDescribing ? "Describing…" : "Describe with AI"}
              </Button>
            </div>
          )}

          {/* Documents */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Documents</span>
              <span className="text-xs text-gray-400">
                ({documents.length}/{MAX_DOCUMENTS})
              </span>
            </div>

            {documents.length > 0 && (
              <div className="space-y-2 mb-3">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <ResourceDocumentThumb doc={doc} brand={brand} className="mt-1" />
                    <div className="min-w-0 flex-1">
                      <input
                        type="text"
                        value={doc.label}
                        onChange={(e) => handleUpdateDocument(doc.id, { label: e.target.value })}
                        placeholder={doc.fileName}
                        className="w-full bg-transparent text-base sm:text-sm font-medium text-gray-900 focus:outline-none"
                      />
                      <div className="text-xs text-gray-500 truncate">
                        {doc.fileName}
                        {formatSize(doc.sizeBytes) ? ` · ${formatSize(doc.sizeBytes)}` : ""}
                      </div>
                      {renderDescription(doc, DOCUMENT_DESCRIPTION_PLACEHOLDER, handleUpdateDocument)}
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleRemoveDocument(doc)}
                      className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
                      aria-label="Remove document"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden"
              onChange={(e) => void handleFileChange(e)}
            />
            {documents.length < MAX_DOCUMENTS ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handlePickFile}
                disabled={isUploading}
                className="gap-1"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {uploadProgress && uploadProgress.total > 1
                      ? `Uploading ${uploadProgress.current} of ${uploadProgress.total}…`
                      : "Uploading…"}
                  </>
                ) : (
                  <>
                    <Upload className="w-3.5 h-3.5" />
                    Upload Documents
                  </>
                )}
              </Button>
            ) : (
              <p className="text-xs text-amber-600">Maximum of {MAX_DOCUMENTS} documents reached</p>
            )}

            {agent.id && documents.length < MAX_DOCUMENTS && (
              <div className="mt-3">
                <KbResourceSuggestions
                  agentId={agent.id}
                  offeredKeys={offeredKeys}
                  offeredFileNames={offeredFileNames}
                  slots={MAX_DOCUMENTS - documents.length}
                  onImported={handleImportedFromKb}
                />
              </div>
            )}
          </div>

          {/* Banner image */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Image className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Banner Image (optional)</span>
            </div>
            <input
              type="url"
              value={bannerImageUrl}
              onChange={(e) => setConfig((prev) => ({ ...prev, banner_image_url: e.target.value || null }))}
              placeholder="https://example.com/banner.png"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {bannerImageUrl && (
              <div className="mt-2 rounded-lg overflow-hidden border border-gray-200 max-w-xs">
                <img
                  src={bannerImageUrl}
                  alt="Banner preview"
                  className="w-full h-auto"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            )}
          </div>

          {/* Links */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <LinkIcon className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Links</span>
              <span className="text-xs text-gray-400">
                ({infoLinks.length}/{MAX_LINKS})
              </span>
            </div>

            {committedLinks.length > 0 && (
              <div className="space-y-2 mb-3">
                {committedLinks.map((link) => (
                  <div
                    key={link.id}
                    className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <ResourceLinkThumb link={link} brand={brand} className="mt-1" />
                    <div className="min-w-0 flex-1">
                      <input
                        type="text"
                        value={link.label}
                        onChange={(e) => handleUpdateLink(link.id, { label: e.target.value })}
                        placeholder={link.url}
                        className="w-full bg-transparent text-base sm:text-sm font-medium text-gray-900 focus:outline-none"
                      />
                      <div className="text-xs text-gray-500 truncate">{link.url}</div>
                      {renderDescription(link, LINK_DESCRIPTION_PLACEHOLDER, handleUpdateLink)}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveLink(link.id)}
                      className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
                      aria-label="Remove link"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {infoLinks.length < MAX_LINKS ? (
              <div className="space-y-2">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={newLinkLabel}
                    onChange={(e) => setNewLinkLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddLink();
                      }
                    }}
                    placeholder="Label"
                    className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm"
                  />
                  <input
                    type="url"
                    value={newLinkUrl}
                    onChange={(e) => {
                      setNewLinkUrl(e.target.value);
                      if (linkUrlError) setLinkUrlError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddLink();
                      }
                    }}
                    placeholder="https://example.com"
                    className={`min-w-0 flex-1 rounded-md border px-3 py-2 text-base sm:text-sm ${linkUrlError ? "border-red-300" : "border-gray-300"}`}
                  />
                </div>
                {linkUrlError && <p className="text-xs text-red-500">{linkUrlError}</p>}
                <textarea
                  value={newLinkDescription}
                  onChange={(e) => setNewLinkDescription(e.target.value)}
                  placeholder="What this page covers (optional — leave empty and AI will write it)"
                  maxLength={RESOURCE_DESCRIPTION_MAX}
                  rows={2}
                  className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm"
                />
              </div>
            ) : (
              <p className="text-xs text-amber-600">Maximum of {MAX_LINKS} links reached</p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {infoLinks.length < MAX_LINKS && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddLink}
                  disabled={!newLinkLabel.trim() || !newLinkUrl.trim()}
                  className="gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Link
                </Button>
              )}
              <Button type="button" variant="outline" size="sm" onClick={() => setShowBulkPaste(true)} className="gap-1">
                <ListPlus className="w-3.5 h-3.5" />
                Add in bulk
              </Button>
            </div>

            {agent.id && (
              <div className="mt-3">
                <KnowledgeLinkSuggestions
                  agentId={agent.id}
                  brand={brand}
                  existingLinks={committedLinks}
                  dismissedKeys={config.dismissedLinks ?? []}
                  slots={MAX_LINKS - infoLinks.length}
                  onAdd={handleAddKnowledgeLinks}
                  onDismiss={handleDismissKnowledgeLinks}
                />
              </div>
            )}
          </div>
          <BulkLinksPasteSheet
            isOpen={showBulkPaste}
            onClose={() => setShowBulkPaste(false)}
            existingLinks={committedLinks}
            maxLinks={MAX_LINKS}
            genieName={agent.name}
            onApply={handleApplyBulkLinks}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {savedLinks.length === 0 && savedDocuments.length === 0 && !savedConfig.banner_image_url ? (
            <p className="text-sm text-gray-400 italic">No resources configured</p>
          ) : (
            <>
              {savedMissingCount > 0 && (
                <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                  <div className="flex flex-col gap-3 rounded-[10px] bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {savedMissingCount} resource{savedMissingCount === 1 ? " has" : "s have"} no description
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Your Genie can't tell what's inside, so {savedMissingCount === 1 ? "it" : "they"} may not show up
                        at the right moment. AI can read them for you — you review before saving.
                      </p>
                    </div>
                    <Button type="button" size="sm" onClick={handleDescribeSaved} className={`${AI_BUTTON_CLASS} min-h-[44px] sm:min-h-0 flex-shrink-0`}>
                      <Sparkles className="w-3.5 h-3.5" />
                      Describe with AI
                    </Button>
                  </div>
                </div>
              )}
              {savedConfig.banner_image_url && (
                <div className="rounded-lg overflow-hidden border border-gray-200 max-w-xs mb-2">
                  <img
                    src={savedConfig.banner_image_url}
                    alt="Banner"
                    className="w-full h-auto"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </div>
              )}
              {savedDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <ResourceDocumentThumb doc={doc} brand={brand} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">{doc.label || doc.fileName}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {doc.fileName}
                      {formatSize(doc.sizeBytes) ? ` · ${formatSize(doc.sizeBytes)}` : ""}
                    </div>
                    {doc.description && <div className="text-xs text-gray-600 mt-1 line-clamp-2">{doc.description}</div>}
                  </div>
                </div>
              ))}
              {savedLinks.map((link) => (
                <div
                  key={link.id}
                  className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <ResourceLinkThumb link={link} brand={brand} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">{link.label}</div>
                    <div className="text-xs text-gray-500 truncate">{link.url}</div>
                    {link.description && <div className="text-xs text-gray-600 mt-1 line-clamp-2">{link.description}</div>}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </OverviewSectionCard>
  );
};
